import React, { useState} from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, CheckCircle, AlertTriangle} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TestPanel = ({ versionId, token }) => {
  const [inputs, setInputs] = useState({
    type_acteur: 'Particulier',
    territoire: 'Guadeloupe',
    secteur: 'Habitat',
    revenu_mensuel: 2500,
    montant_projet: 150000,
    apport: 15000,
    duree_mois: 180
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const runTest = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/versions/${versionId}/dry-run`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ inputs })
      });
      if (res.ok) {
        setResult(await res.json());
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Inputs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">🧪 Paramètres de test</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Type d'acteur</label>
              <select 
                value={inputs.type_acteur}
                onChange={e => setInputs({...inputs, type_acteur: e.target.value})}
                className="w-full border rounded-lg p-2 mt-1"
              >
                <option>Particulier</option>
                <option>TPE</option>
                <option>Exploitant</option>
                <option>Association</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Territoire</label>
              <select 
                value={inputs.territoire}
                onChange={e => setInputs({...inputs, territoire: e.target.value})}
                className="w-full border rounded-lg p-2 mt-1"
              >
                <option>Guadeloupe</option>
                <option>Martinique</option>
                <option>Guyane</option>
                <option>France Hexagonale</option>
                <option>GCC</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Revenu mensuel (€)</label>
            <input 
              type="number"
              value={inputs.revenu_mensuel}
              onChange={e => setInputs({...inputs, revenu_mensuel: parseInt(e.target.value) || 0})}
              className="w-full border rounded-lg p-2 mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Montant projet (€)</label>
            <input 
              type="number"
              value={inputs.montant_projet}
              onChange={e => setInputs({...inputs, montant_projet: parseInt(e.target.value) || 0})}
              className="w-full border rounded-lg p-2 mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Apport (€)</label>
            <input 
              type="number"
              value={inputs.apport}
              onChange={e => setInputs({...inputs, apport: parseInt(e.target.value) || 0})}
              className="w-full border rounded-lg p-2 mt-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Durée (mois)</label>
            <input 
              type="number"
              value={inputs.duree_mois}
              onChange={e => setInputs({...inputs, duree_mois: parseInt(e.target.value) || 0})}
              className="w-full border rounded-lg p-2 mt-1"
            />
          </div>

          <Button onClick={runTest} className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Exécuter le test
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">📊 Résultats</CardTitle>
        </CardHeader>
        <CardContent>
          {result ? (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${
                result.status === 'blocked' ? 'bg-red-50 border border-red-200' :
                result.status === 'ok_with_limits' ? 'bg-yellow-50 border border-yellow-200' :
                'bg-green-50 border border-green-200'
              }`}>
                <div className="flex items-center gap-2">
                  {result.status === 'blocked' ? (
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  ) : result.status === 'ok_with_limits' ? (
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  )}
                  <span className="font-bold text-lg">
                    {result.status === 'blocked' ? 'BLOQUÉ' :
                     result.status === 'ok_with_limits' ? 'OK AVEC LIMITES' : 'OK'}
                  </span>
                </div>
                
                {result.hard_block && (
                  <p className="mt-2 text-sm text-red-700">
                    {result.hard_block.messages?.fr || result.hard_block.name}
                  </p>
                )}
              </div>

              {/* Caps applied */}
              {Object.keys(result.caps || {}).length > 0 && (
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <p className="font-medium text-sm mb-2">Plafonds appliqués:</p>
                  {Object.entries(result.caps).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm">
                      <span>{k}</span>
                      <span className="font-mono font-bold">{v}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Soft/ESS messages */}
              {result.soft_applied?.length > 0 && (
                <div className="space-y-2">
                  {result.soft_applied.map((s, i) => (
                    <div key={i} className="p-2 bg-yellow-50 rounded text-sm border-l-2 border-yellow-400">
                      <span className="font-medium">{s.rule_key}:</span> {s.messages?.fr}
                    </div>
                  ))}
                </div>
              )}

              {result.ess_applied?.length > 0 && (
                <div className="space-y-2">
                  {result.ess_applied.map((s, i) => (
                    <div key={i} className="p-2 bg-purple-50 rounded text-sm border-l-2 border-purple-400">
                      <span className="font-medium">{s.rule_key}:</span> {s.messages?.fr}
                    </div>
                  ))}
                </div>
              )}

              {/* Evaluation logs */}
              <details className="text-xs">
                <summary className="cursor-pointer text-gray-500 hover:text-gray-700">Logs d'évaluation</summary>
                <pre className="mt-2 p-2 bg-gray-100 rounded overflow-auto max-h-48">
                  {result.evaluation_logs?.join('\n')}
                </pre>
              </details>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              Lancez un test pour voir les résultats
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

